'use memo'

import {
  type Dispatch,
  type SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react'
import { MenuTitles, Pages } from 'constants/menu'
import { ContentLayout, PageLayout } from 'layout'
import {
  Outlet,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom'
import { DragEndEvent } from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import { AgxSortableList } from '@urbanx/agx-ui-components'
import {
  AgxButton,
  AgxLoadingState,
  AgxSpacer,
  AgxTypography,
  Icon_ListOutline,
  Icon_More,
  Icon_PlusOutline,
  AgxEmptyState,
} from '@urbanx/agx.ui-components2'
import { AuthContext } from '@urbanx/agx.auth.client'
import {
  type AgencyDetailsModel,
  useGetAgencySuppliers,
  useInvalidateUseGetAgencySuppliers,
  useStaffGetAgentsInAgency,
} from '@urbanx/agx.agencies.client'
import { useSelectedAgency } from 'hooks'
import {
  type MarketingItemTemplateModel,
  type MarketingPackageTemplateModel,
  useCreateMarketingPackageTemplate,
  useDeleteMarketingPackageTemplate,
  useInvalidateUseStaffGetMarketingPackageTemplates,
  useStaffGetMarketingItemTemplates,
  useStaffGetMarketingPackageTemplates,
  useUpdateMarketingPackageTemplateOrder,
} from '@urbanx/agx.marketing.client'
import DeleteConfirmation from 'components/delete-confirmation/DeleteConfirmation'
import ListItemOptions from 'components/options/ListItemOptions'
import MarketingItems from './components/marketing-items/MarketingItems'
import MarketingPackages from './components/marketing-packages/marketingPackages'
import AgxAgencySuppliers from './components/agency-suppliers/AgencySuppliers'
import './marketing.scss'
import MarketingPanel from './MarketingPanel'

type MarketingPackageAction = 'Duplicate' | 'Delete'

interface MarketingRouteContext {
  agencyId: string
  selectedAgency?: AgencyDetailsModel
  isLoadingSelectedAgency: boolean
}

interface MarketingPackagesResult {
  currentMarketingPackages: MarketingPackageTemplateModel[]
  setMarketingPackages: Dispatch<
    SetStateAction<MarketingPackageTemplateModel[]>
  >
  isLoadingMarketingPackages: boolean
}

const convertMarketingItemTemplateToIMarketingItemTemplateModel = (
  marketingItemTemplates: MarketingItemTemplateModel[]
) => {
  return marketingItemTemplates.map(
    (item) =>
      ({
        id: item.id,
        productName: item.productName,
        pricePerUnit: item.pricePerUnit,
        itemType: item.itemType,
        supplierId: item.supplierId,
        description: item.description,
        category: item.category,
        order: item.order,
        templateType: item.templateType,
        groupItems: item.groupItems,
      }) as MarketingItemTemplateModel
  )
}

const useMarketingRouteContext = () => {
  return useOutletContext<MarketingRouteContext>()
}

const getMarketingRootPath = (agencyId: string) =>
  `/${Pages.AGENCIES}/${agencyId}/${Pages.MARKETING}`

const useMarketingPackages = (agencyId: string): MarketingPackagesResult => {
  const [currentMarketingPackages, setMarketingPackages] = useState<
    MarketingPackageTemplateModel[]
  >([])

  const { data: marketingPackages, isPending: isLoadingMarketingPackages } =
    useStaffGetMarketingPackageTemplates(agencyId, !!agencyId)

  useEffect(() => {
    if (marketingPackages) setMarketingPackages(marketingPackages)
  }, [marketingPackages, isLoadingMarketingPackages])

  return {
    currentMarketingPackages,
    setMarketingPackages,
    isLoadingMarketingPackages,
  }
}

const MarketingPage = () => {
  const { agencyId } = useParams()
  const { userAccount } = useContext(AuthContext)
  const { agency: selectedAgency, isLoading: isLoadingSelectedAgency } =
    useSelectedAgency()

  const context: MarketingRouteContext = {
    agencyId: agencyId || '',
    selectedAgency,
    isLoadingSelectedAgency,
  }

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={selectedAgency?.name || ''}
      currentPageTitle={MenuTitles.MARKETING}
    >
      <ContentLayout
        hasSecondaryMenu={true}
        secondaryActiveMenu={MenuTitles.MARKETING}
      >
        <AgxSpacer direction="horizontal" className="stretchContainerItems">
          <Outlet context={context} />
        </AgxSpacer>
      </ContentLayout>
    </PageLayout>
  )
}

export const MarketingDashboard = () => {
  const navigate = useNavigate()
  const {
    agencyId,
    isLoadingSelectedAgency,
  } = useMarketingRouteContext()
  const { data: agencySuppliers, isPending: isLoadingAgencySuppliers } =
    useGetAgencySuppliers(agencyId, !!agencyId)
  const { data: marketingItems, isPending: isLoadingMarketingItems } =
    useStaffGetMarketingItemTemplates(agencyId, !!agencyId)
  const currentMarketingItems = marketingItems ?? []
  const {
    currentMarketingPackages,
    setMarketingPackages,
    isLoadingMarketingPackages,
  } = useMarketingPackages(agencyId)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(
    null
  )

  const invalidateUseStaffGetMarketingPackageTemplates =
    useInvalidateUseStaffGetMarketingPackageTemplates()

  const {
    createMarketingPackageTemplate,
    createMarketingPackageTemplatePending,
  } = useCreateMarketingPackageTemplate({
    onSuccess: () => {
      invalidateUseStaffGetMarketingPackageTemplates(agencyId)
    },
    onError: () => {},
  })

  const {
    updateMarketingPackageTemplateOrder,
    updateMarketingPackageTemplateOrderPending,
  } = useUpdateMarketingPackageTemplateOrder({
    onSuccess: () => {
      invalidateUseStaffGetMarketingPackageTemplates(agencyId)
    },
    onError: () => {},
  })

  const {
    deleteMarketingPackageTemplate,
    deleteMarketingPackageTemplatePending,
  } = useDeleteMarketingPackageTemplate({
    onSuccess: () => {
      invalidateUseStaffGetMarketingPackageTemplates(agencyId)
      setShowDeleteConfirmation(false)
    },
    onError: () => {},
  })

  const onEditMarketingPackage = (mp: MarketingPackageTemplateModel) => {
    if (!mp.id) return

    navigate(`./package/${mp.id}`)
  }

  const onDeletePackageClick = (mp: MarketingPackageTemplateModel) => {
    setSelectedPackageId(mp.id ?? null)
    setShowDeleteConfirmation(true)
  }

  const onDuplicatePackageClick = async (mp: MarketingPackageTemplateModel) => {
    await createMarketingPackageTemplate({
      agencyId,
      order: currentMarketingPackages.length,
      name: `${mp.name} - Copy`,
      displayName: mp.displayName,
      includeRealEstateListing: mp.includeRealEstateListing,
      includeDomainListing: mp.includeDomainListing,
      displayItemisedPricingOnPdf: mp.displayItemisedPricingOnPdf,
      items: mp.items,
      addOns: mp.addOns,
      specialConditions: mp.specialConditions,
      assignedAgentIds: mp.assignedAgentIds,
    })
  }

  const onConfirmDelete = async () => {
    await deleteMarketingPackageTemplate({
      agencyId,
      marketingPackageId: selectedPackageId || '',
    })
  }

  const handleOnDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    setMarketingPackages((prev) => {
      const oldIndex = prev.findIndex((item) => item.id === active.id)
      const newIndex = prev.findIndex((item) => item.id === over.id)
      const updatedPackages = arrayMove(prev, oldIndex, newIndex)

      const marketingPackageIds = updatedPackages
        .map((item) => item.id)
        .filter((id): id is string => !!id)

      updateMarketingPackageTemplateOrder({
        marketingPackageIds,
      })

      return updatedPackages
    })
  }

  const renderMarketingPackages = (
    mp: MarketingPackageTemplateModel,
    index: number
  ) => {
    const marketingPackageActions = [
      { action: 'Duplicate', children: 'Duplicate' },
      { action: 'Delete', children: 'Delete' },
    ] satisfies {
      action: MarketingPackageAction
      children: string
    }[]

    return (
      <AgxSpacer
        direction="horizontal"
        contentJustification="space-between"
        fill
        contentAlignment="center"
        key={mp.id || `marketingItem-${mp.id}-${index}`}
        id={mp.id || `marketingItem-${mp.id}-${index}`}
        className="marketingPackageItem"
      >
        <AgxTypography
          id={`marketing-package-name-${mp.id || index}`}
          type="heading"
          size="x-small"
        >
          {mp.name}
        </AgxTypography>

        <AgxSpacer
          direction="horizontal"
          contentAlignment="center"
          contentJustification="end"
          style={{
            width: 'min-content',
          }}
          gap="md"
        >
          <AgxButton
            id={`marketing-package-manage-${mp.id || index}`}
            text="Manage"
            size="medium"
            variant="hollow"
            disabled={
              !mp.id ||
              isLoadingMarketingItems ||
              isLoadingMarketingPackages ||
              isLoadingSelectedAgency ||
              createMarketingPackageTemplatePending ||
              updateMarketingPackageTemplateOrderPending ||
              deleteMarketingPackageTemplatePending
            }
            onClick={() => onEditMarketingPackage(mp)}
          />
          {mp.id && (
            <ListItemOptions
              actions={marketingPackageActions}
              id={`marketing-package-options-menu-${mp.id}`}
              dataTestId={`marketing-package-options-${mp.id || index}`}
              trigger={
                <span
                  aria-label="Marketing package options"
                  className="marketingPackageOptionsIcon"
                >
                  <Icon_More />
                </span>
              }
              onAction={(action) => {
                if (action === 'Duplicate') {
                  onDuplicatePackageClick(mp)
                  return
                }

                onDeletePackageClick(mp)
              }}
            />
          )}
        </AgxSpacer>
      </AgxSpacer>
    )
  }

  const selectedPackage = selectedPackageId
    ? currentMarketingPackages.find((pkg) => pkg.id === selectedPackageId)
    : undefined

  return (
    <>
      <div className="marketingGrid">
        {isLoadingMarketingItems ||
        isLoadingAgencySuppliers ||
        isLoadingSelectedAgency ? (
          <AgxEmptyState variant="minimal" label="loading..." />
        ) : (
          <MarketingPanel>
            <AgxSpacer direction="vertical" gap="2xs">
              <AgxTypography
                id="marketing-items-count"
                type="heading"
                size="2x-large"
                color="var(--text-primary)"
              >
                {currentMarketingItems.length}
              </AgxTypography>
              <AgxTypography
                id="marketing-items-title"
                type="body"
                size="medium"
                color="var(--text-primary)"
              >
                Marketing Items
              </AgxTypography>
            </AgxSpacer>
            <AgxButton
              id="marketing-items-view"
              text="View Items"
              size="medium"
              variant="primary"
              rightIcon={<Icon_ListOutline color="currentColor" />}
              onClick={() => navigate('./items')}
              disabled={
                isLoadingMarketingItems ||
                isLoadingAgencySuppliers ||
                isLoadingSelectedAgency
              }
            />
          </MarketingPanel>
        )}
        {isLoadingAgencySuppliers ? (
          <AgxEmptyState variant="minimal" label="loading..." />
        ) : (
          <MarketingPanel>
            <AgxSpacer direction="vertical" gap="2xs">
              <AgxTypography
                id="marketing-suppliers-count"
                type="heading"
                size="2x-large"
                color="var(--text-primary)"
              >
                {agencySuppliers?.suppliers?.length ?? 0}
              </AgxTypography>
              <AgxTypography
                id="marketing-suppliers-title"
                type="body"
                size="medium"
                color="var(--text-primary)"
              >
                Suppliers
              </AgxTypography>
            </AgxSpacer>

            <AgxButton
              id="marketing-suppliers-view"
              text="View Suppliers"
              size="medium"
              variant="primary"
              rightIcon={<Icon_ListOutline color="currentColor" />}
              onClick={() => navigate('./suppliers')}
              disabled={isLoadingAgencySuppliers}
            />
          </MarketingPanel>
        )}

        <MarketingPanel fullWidth>
          {isLoadingMarketingPackages ||
          isLoadingMarketingItems ||
          isLoadingSelectedAgency ? (
            <AgxEmptyState variant="minimal" label="loading..." />
          ) : (
            <>
              <AgxSpacer direction="vertical" gap="2xs">
                <AgxTypography
                  id="marketing-packages-count"
                  type="heading"
                  size="2x-large"
                  color="var(--text-primary)"
                >
                  {currentMarketingPackages.length}
                </AgxTypography>
                <AgxTypography
                  id="marketing-packages-title"
                  type="body"
                  size="medium"
                  color="var(--text-primary)"
                >
                  Marketing Package Templates
                </AgxTypography>
              </AgxSpacer>
              <AgxSpacer direction="vertical" fill>
                <AgxSortableList
                  items={currentMarketingPackages}
                  getId={(item) => item.id || `marketingItem-${item.id}`}
                  renderItem={(item, index) =>
                    renderMarketingPackages(item, index)
                  }
                  onDragEnd={handleOnDragEnd}
                  extraClasses="marketingPackagesListItem"
                />
              </AgxSpacer>
              <AgxButton
                id="marketing-package-new"
                text="New Package"
                size="medium"
                variant="primary"
                rightIcon={<Icon_PlusOutline color="currentColor" />}
                onClick={() => navigate('./package/new')}
                disabled={
                  isLoadingMarketingPackages ||
                  isLoadingMarketingItems ||
                  isLoadingSelectedAgency
                }
              />
            </>
          )}
        </MarketingPanel>
      </div>

      <DeleteConfirmation
        name={selectedPackage?.name || ''}
        title="Delete marketing package?"
        lineOne="Are you sure you want to delete"
        lineTwo="This cannot be undone."
        primaryActionTitle="Permanently delete package"
        secondaryActionTitle="No, cancel"
        showPopUp={showDeleteConfirmation}
        onClose={() => {
          setShowDeleteConfirmation(false)
        }}
        onConfirm={() => onConfirmDelete()}
      />
    </>
  )
}

export const MarketingItemsPage = () => {
  const navigate = useNavigate()
  const { agencyId } = useMarketingRouteContext()
  const { data: agencySuppliers, isPending: isLoadingAgencySuppliers } =
    useGetAgencySuppliers(agencyId, !!agencyId)
  const { data: marketingItems, isPending: isLoadingMarketingItems } =
    useStaffGetMarketingItemTemplates(agencyId, !!agencyId)
  const currentMarketingItems = marketingItems ?? []

  if (!agencyId || isLoadingAgencySuppliers || isLoadingMarketingItems) {
    return <AgxLoadingState />
  }

  if (!agencySuppliers) {
    return <AgxEmptyState variant="minimal" label="loading..." />
  }

  return (
    <MarketingItems
      onBack={() => navigate('../')}
      existingItems={convertMarketingItemTemplateToIMarketingItemTemplateModel(
        currentMarketingItems
      )}
      agencyId={agencyId}
      agencySuppliers={agencySuppliers.suppliers ?? []}
    />
  )
}

export const MarketingSuppliersPage = () => {
  const navigate = useNavigate()
  const { agencyId } = useMarketingRouteContext()
  const invalidateUseGetAgencySuppliers = useInvalidateUseGetAgencySuppliers()
  const { data: agencySuppliers, isPending: isLoadingAgencySuppliers } =
    useGetAgencySuppliers(agencyId, !!agencyId)

  const onSuppliersChanged = () => {
    if (agencyId) void invalidateUseGetAgencySuppliers(agencyId)
  }

  if (!agencyId || isLoadingAgencySuppliers) {
    return <AgxLoadingState />
  }

  if (!agencySuppliers) {
    return <AgxEmptyState variant="minimal" label="loading..." />
  }

  return (
    <AgxAgencySuppliers
      agencySuppliers={agencySuppliers}
      onSuppliersChanged={onSuppliersChanged}
      onBack={() => navigate('../')}
    />
  )
}

type MarketingPackageEditorPageProps =
  | { mode: 'new' }
  | { mode: 'edit'; marketingPackageId?: string }

const MarketingPackageEditorPage = (props: MarketingPackageEditorPageProps) => {
  const navigate = useNavigate()
  const {
    agencyId,
    isLoadingSelectedAgency,
  } = useMarketingRouteContext()
  const { data: agents, isPending: isPendingAgents } =
    useStaffGetAgentsInAgency(agencyId, !!agencyId)
  const { data: marketingItems, isPending: isLoadingMarketingItems } =
    useStaffGetMarketingItemTemplates(agencyId, !!agencyId)
  const currentMarketingItems = marketingItems ?? []
  const { currentMarketingPackages, isLoadingMarketingPackages } =
    useMarketingPackages(agencyId)
  const activeAgents = (agents ?? []).filter((agent) => !agent.softDeleted)
  const selectedPackage =
    props.mode === 'edit'
      ? currentMarketingPackages.find(
          (pkg) => pkg.id === props.marketingPackageId
        )
      : undefined
  const isMarketingPackageEditorLoading =
    isLoadingMarketingItems ||
    isPendingAgents ||
    isLoadingSelectedAgency ||
    isLoadingMarketingPackages

  if (isMarketingPackageEditorLoading) {
    return <AgxLoadingState />
  }

  if (props.mode === 'edit' && !selectedPackage) {
    return (
      <AgxSpacer
        direction="vertical"
        contentAlignment="center"
        contentJustification="center"
        fill
        gap="md"
      >
        <AgxEmptyState
          variant="minimal"
          label="Marketing package not found"
        />
        <AgxButton
          id="marketing-package-not-found-back"
          text="Back to Marketing"
          size="medium"
          variant="primary"
          onClick={() => navigate(getMarketingRootPath(agencyId))}
        />
      </AgxSpacer>
    )
  }

  return (
    <MarketingPackages
      onBack={() => navigate(getMarketingRootPath(agencyId))}
      marketingPackage={selectedPackage}
      marketingPackageOrder={
        !selectedPackage
          ? currentMarketingPackages.length + 1
          : (selectedPackage.order ?? 0)
      }
      includedStandardInclusions={selectedPackage?.items || []}
      includedAddons={selectedPackage?.addOns || []}
      allItems={currentMarketingItems}
      allAvailableAgents={activeAgents}
      agencyId={agencyId}
    />
  )
}

export const MarketingNewPackagePage = () => {
  return <MarketingPackageEditorPage mode="new" />
}

export const MarketingEditPackagePage = () => {
  const { marketingPackageId } = useParams()

  return (
    <MarketingPackageEditorPage
      mode="edit"
      marketingPackageId={marketingPackageId}
    />
  )
}

export default MarketingPage
